/**
 * Action Orchestrator Type Definitions
 *
 * Core types for the action orchestrator system including action identifiers,
 * status states, and result structures.
 */

/**
 * Enumeration of all available actions in the system.
 * Actions follow snake_case naming convention (e.g., login, get_profile, update_card).
 *
 * SYNC: Keep synchronized with Go enum at
 * apps/consumer/processors/factory/types/types.go
 */
export enum Action {
  // Auth actions
  LOGIN = 'login',
  LOGOUT = 'logout',

  // User actions
  CREATE_USER = 'create_user',
  DELETE_USER = 'delete_user',
  GET_PREFERENCES = 'get_preferences',
  GET_ORGANIZATION_USERS = 'get_organization_users',
  RESET_USER_PASSWORD = 'reset_user_password',
  UPDATE_USER = 'update_user',
  UPDATE_USER_PREFERENCES = 'update_user_preferences',
  UPDATE_PROFILE_PICTURE = 'update_profile_picture',
  GET_USER_PROFILE = 'get_user_profile',

  // Organization actions
  CREATE_ORGANIZATION = 'create_organization',
  DELETE_ORGANIZATION = 'delete_organization',
  GET_ALL_ORGANIZATIONS = 'get_all_organizations',
  GET_ORG_CONFIGURATIONS = 'get_org_configurations',
  RESTORE_ORGANIZATION = 'restore_organization',
  SWITCH_ORGANIZATION = 'switch_organization',
  UPDATE_ORGANIZATION = 'update_organization',
  UPDATE_ORG_CONFIGURATION = 'update_org_configuration',
  UPDATE_ORG_PROFILE_PICTURE = 'update_org_profile_picture',
  LIST_ORG_PROFILE_PICTURES = 'list_org_profile_pictures',

  // Storage actions
  GET_PRESIGNED_UPLOAD_URL = 'get_presigned_upload_url',
  LIST_PROFILE_PICTURES = 'list_profile_pictures',
  LIST_BOARD_BACKGROUNDS = 'list_board_backgrounds',

  // User group actions
  CREATE_USER_GROUP = 'create_user_group',
  DELETE_USER_GROUP = 'delete_user_group',
  GET_USER_GROUPS = 'get_user_groups',
  ADD_USER_GROUP_MEMBER = 'add_user_group_member',
  BULK_ADD_USER_GROUP_MEMBERS = 'bulk_add_user_group_members',
  REMOVE_USER_GROUP_MEMBER = 'remove_user_group_member',
  GET_USER_GROUP_MEMBERS = 'get_user_group_members',
  GET_USER_GROUP_MEMBERS_BULK = 'get_user_group_members_bulk',

  // Board actions
  CREATE_BOARD = 'create_board',
  CREATE_BOARD_FROM_TEMPLATE = 'create_board_from_template',
  CREATE_BOARD_STATUS = 'create_board_status',
  CREATE_BOARD_TEMPLATE = 'create_board_template',
  CREATE_TASK = 'create_task',
  CREATE_TASK_RELATION = 'create_task_relation',
  CREATE_COMMENT = 'create_comment',
  DELETE_BOARD = 'delete_board',
  DELETE_BOARD_STATUS = 'delete_board_status',
  DELETE_BOARD_TEMPLATE = 'delete_board_template',
  DELETE_TASK = 'delete_task',
  DELETE_TASK_RELATION = 'delete_task_relation',
  GET_ALL_BOARDS = 'get_all_boards',
  GET_BOARD = 'get_board',
  GET_BOARD_PREFERENCES = 'get_board_preferences',
  GET_BOARD_TEMPLATES = 'get_board_templates',
  GET_TASK = 'get_task',
  GET_TASK_ASSIGNMENTS = 'get_task_assignments',
  GET_TASK_RELATIONS = 'get_task_relations',
  GET_COMMENTS = 'get_comments',
  RESTORE_BOARD = 'restore_board',
  RESTORE_BOARD_STATUS = 'restore_board_status',
  RESTORE_BOARD_TEMPLATE = 'restore_board_template',
  RESTORE_TASK = 'restore_task',
  SAVE_BOARD_AS_TEMPLATE = 'save_board_as_template',
  SEARCH_USERS = 'search_users',
  UPDATE_BOARD = 'update_board',
  UPDATE_BOARD_TEMPLATE = 'update_board_template',
  UPDATE_BOARD_BACKGROUND = 'update_board_background',
  UPDATE_BOARD_STATUS_CONFIG = 'update_board_status_config',
  UPDATE_TASK = 'update_task',
  UPDATE_TASK_STATUS = 'update_task_status',

  // Dashboard actions
  GET_MY_PROGRESS = 'get_my_progress',
  GET_DASHBOARD_STATS = 'get_dashboard_stats',
  GET_LEARNING_PROGRESS_STATS = 'get_learning_progress_stats',

  // Scheduled jobs actions
  GET_SCHEDULED_JOBS = 'get_scheduled_jobs',
  CREATE_SCHEDULED_JOB = 'create_scheduled_job',
  UPDATE_SCHEDULED_JOB = 'update_scheduled_job',
  RUN_SCHEDULED_JOB = 'run_scheduled_job',
  GET_JOB_RUN = 'get_job_run',
  GET_JOB_RUNS = 'get_job_runs',
  CANCEL_JOB_RUN = 'cancel_job_run',

  // Cohort actions
  GET_COHORT_BOARD = 'get_cohort_board',
  GET_COHORT_PROGRESS = 'get_cohort_progress',
  SYNC_COHORTS = 'sync_cohorts',

  // Notification actions
  GET_NOTIFICATIONS = 'get_notifications',
  MARK_NOTIFICATION_READ = 'mark_notification_read',

  // Test actions
  NO_OP = 'no_op',
}

/**
 * Status of an action execution.
 */
export enum ActionStatus {
  /** Action has not started */
  IDLE = 'idle',
  /** Action is currently executing */
  IN_PROGRESS = 'in_progress',
  /** Action completed successfully */
  SUCCESS = 'success',
  /** Action failed with an error */
  ERROR = 'error',
}

/**
 * Result of a processor execution.
 * Neutral response - just code and optional data.
 * The orchestrator interprets the code to determine success/error.
 *
 * @template TOutput The type of data returned
 */
export interface ProcessorResult<TOutput = unknown> {
  /** Response code indicating the result */
  code: import('./response-codes.types').ResponseCode;
  /** Data returned by the processor (present for success codes) */
  data?: TOutput;
}

/**
 * DEPRECATED: Use ProcessorResult instead.
 * Kept for backward compatibility during migration.
 */
export interface ActionResult<TData = unknown> {
  /** Current status of the action */
  status: ActionStatus;
  /** Data returned on success (only present when status is SUCCESS) */
  data?: TData;
  /** Error information (only present when status is ERROR) */
  error?: import('./error.types').ActionError;
  /** Additional metadata about the action execution */
  metadata?: {
    /** Async request ID (only present for async executions) */
    asyncRequestId?: string;
    /** Other metadata fields */
    [key: string]: unknown;
  };
}

/**
 * Callback function for tracking action progress.
 *
 * @param progress Progress value between 0 and 100
 * @param message Optional message describing current progress
 */
export type ProgressCallback = (progress: number, message?: string) => void;
